import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CmsService, ImageType, Product } from '@spartacus/core';
import { Observable, of, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import {
  BrandsEnum,
  BrandThemesEnum, IComponentWrapper,
  ImageRatioEnum, IPushCard,
  IPushCardWithLayout,
  PositionEnum, PushSizeEnum, SdComponentWrapperComponent, SdPushCardComponent, SizesEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import { Bundle, CustomBaseStore, PdpConfig, PushCardComponentInterface } from '@seb/web-common/core/models';
import { StockStatusEnum } from '@seb/web-common/core/enums';
import { BrandCornerMapper, BundleMapper, PushCardMapper } from '@seb/web-common/core/mappers';
import { BaseStoreService, TrackingService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-products-and-bundles',
  templateUrl: './products-and-bundles.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdPushCardComponent
  ]
})
export class ProductsAndBundlesComponent implements OnInit, OnDestroy {
  mediaConfig = PositionEnum.top;
  textConfig = PositionEnum.center;
  themesConfig = ThemesEnum.light;
  imageRatio = ImageRatioEnum.r16_9;
  pushSizeEnum = PushSizeEnum;

  promoPush: IPushCardWithLayout;
  wrapperContent: IComponentWrapper;
  lameConfig: PdpConfig;
  pushes;
  promotionPushHasBeenViewed = false;
  promotionName: string;
  isPushPromo = false;
  isBundle = false;
  isBundlesType = false;
  protected readonly destroy$: Subject<void> = new Subject();
  private baseStore: CustomBaseStore;

  @Input() brand = BrandsEnum.default;

  @Input() set product(currentProduct: Product) {
    this.isBundle = false;
    if (currentProduct.bundles) {
      this.isBundlesType = true;
      this.pushes = currentProduct.bundles
        ?.filter((bundle) =>
          bundle.products?.every(
            (product) =>
              (product.price?.value !== null && product.price?.value !== undefined) &&
              product.onSale &&
              (product.stock?.stockLevelStatus === StockStatusEnum.IN_STOCK ||
                product.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK)
          )
        )
        .map((bundle) => this.getPushData(bundle, this.baseStore.displayTaxesIncluded));
    } else {
      this.pushes = undefined;
    }
  }

  @Input() set bundle(currentBundle: Bundle) {
    this.isBundle = true;
    if (currentBundle) {
      this.isBundlesType = false;
      this.pushes = this.bundleMapper.productsToIPushCards(currentBundle.products, this.baseStore.displayTaxesIncluded);
    }
  }

  @ViewChild('pushCardComponent', { read: ElementRef })
  pushCardComponent: ElementRef;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.promotionPushHasBeenViewed && !!this.promotionName) {
      this.promotionPushHasBeenViewed = this.trackingService.checkingForPromoView(
        this.pushCardComponent,
        this.isPushPromo,
        this.promotionName
      );
    }
  }

  constructor(
    private readonly cmsService: CmsService,
    private readonly pushCardMapper: PushCardMapper,
    private translateService: TranslateService,
    private bundleMapper: BundleMapper,
    private trackingService: TrackingService,
    private cdRef: ChangeDetectorRef,
    private brandCornerMapper: BrandCornerMapper,
    private baseStoreService: BaseStoreService
  ) {
    this.baseStore = this.baseStoreService.baseStore;
  }

  ngOnInit(): void {
    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand)?.pdp || {};
    this.wrapperContent = {
      titleLevel: 2,
      links: [],
      footNotes: [],
      heading: {
        titleLevel: 2,
        titleThemesConfig:
          this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.bundlesOrProducts?.titleTheme || BrandThemesEnum.brand,
        isUnderlined: !!this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.bundlesOrProducts?.titleUnderlined,
        size: SizesEnum.l,
        data: {
          title: this.isBundle ? this.translateService.instant('pdp.sectionTitles.alsoAvailableForBundle')
            : this.translateService.instant('pdp.sectionTitles.alsoAvailable')
        }
      }
    };
    const pushPromo$: Observable<PushCardComponentInterface | undefined> = this.cmsService
      .getContentSlot('PushPromo')
      .pipe(
        switchMap((slot) => {
          const pushUid = slot.components?.find((cmp) => cmp.typeCode === 'PushCardComponent')?.uid;
          return pushUid ? this.cmsService.getComponentData(pushUid) : of(undefined);
        }),
        filter((pushPromo) => !!pushPromo)
      );
    pushPromo$.subscribe((pushPromo) => {
      if (pushPromo) {
        this.promotionName = pushPromo?.headline ? pushPromo?.headline : '';
        this.isPushPromo = pushPromo?.isPushPromo ? JSON.parse(pushPromo?.isPushPromo) : false;
        this.promoPush = this.pushCardMapper.toPushCardWithLayout(pushPromo);
        this.cdRef.markForCheck();
      }
    });
  }

  getPushData(bundle: Bundle, isTaxesIncluded?: boolean): IPushCard {
    const bundleLabel = bundle.saved?.value ? bundle.discountedPrice?.formattedValue : bundle.price?.formattedValue;
    return bundle.galleryImages && !!bundle.galleryImages[ImageType.GALLERY]
      ? {
        richParagraph: {
          heading: {
            data: {
              title: bundle.name,
              subtitle: isTaxesIncluded
                ? bundleLabel +
                      '<br><span class="is-caption is-medium">' +
                      this.translateService.instant('product.taxesIncluded') +
                      '</span>'
                : bundleLabel
            }
          },
          links: [{ url: bundle.canonicalURL }]
        },

        media: bundle.galleryImages[ImageType.GALLERY][0]['main']
      }
      : {
        richParagraph: {
          heading: {
            data: {
              title: bundle.name,
              subtitle: bundle.saved?.value ? bundle.discountedPrice?.formattedValue : bundle.price?.formattedValue
            }
          },

          links: [{ url: bundle.canonicalURL }]
        }
      };
  }

  onBundlePackClick(event: string): void {
    if (this.isBundlesType) {
      this.trackingService.sendEvent('clickPackAvailable', {
        productName: event
      });
    }
  }

  onPromoClick(): void {
    if (this.promotionName) {
      this.trackingService.onPromoClick(this.isPushPromo, this.promotionName);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
